import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Check, ChevronDown, Loader2, Plus, Search, X } from 'lucide-react';
import { Popover } from '../tasks/linear/Popover';
import { useI18n } from '../../contexts/I18nContext';

/**
 * CLE-DASH — custom labeled Select replacing the native browser select elements.
 *
 * Dependency-free (no Radix/headless): the panel is the existing `Popover`
 * portal and the trigger is a `<button>` styled with `.ui-select-trigger`
 * (the `.ui-select` geometry without the CSS chevron, so the lucide
 * ChevronDown doesn't double up). Single-select only — none of the current
 * filters are multi-select. `ProjectSelect` stays separate (specialized).
 *
 * The label is intentionally NOT wired with htmlFor/`<label for>`: clicking a
 * label associated to a button would forward the click and toggle the panel,
 * which surprised users in the pilot (double-toggle feel). Instead the button
 * points back via aria-labelledby / aria-label.
 */

export interface SelectOption {
  value: string;
  label: string;
  /** Hex color rendered as a small dot (colors come from the DB). */
  color?: string;
  icon?: ReactNode;
  description?: string;
  disabled?: boolean;
}

interface SelectProps {
  /** Field label. Always required; use hideLabel to make it screen-reader only. */
  label: string;
  /** Visually hide the label and expose it as aria-label instead. */
  hideLabel?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  /** Show a search header. Defaults to true when there are more than 8 options. */
  searchable?: boolean;
  /** Show an X in the trigger that resets the value to ''. */
  clearable?: boolean;
  size?: 'md' | 'sm';
  error?: string;
  /** Minimum panel width in px (the panel also matches the trigger width). */
  width?: number;
  loading?: boolean;
  emptyText?: string;
  disabled?: boolean;
  /** Extra classes for the outer wrapper. */
  className?: string;
  /**
   * Lets the user add what they typed when no option has that exact label.
   * Resolves to the new option's value, which is then selected. Forces the
   * search header on.
   */
  onCreate?: (query: string) => Promise<string | null | undefined>;
}

export function Select({
  label,
  hideLabel = false,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  searchable,
  clearable = false,
  size = 'md',
  error,
  width = 240,
  loading = false,
  emptyText,
  disabled = false,
  className,
  onCreate,
}: SelectProps) {
  const { t } = useI18n();
  const uid = useId();
  const labelId = `${uid}-label`;
  const listboxId = `${uid}-listbox`;

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const typeahead = useRef({ buffer: '', timer: 0 });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const hasSearch = onCreate ? true : (searchable ?? options.length > 8);
  const sm = size === 'sm';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.description ? o.description.toLowerCase().includes(q) : false),
    );
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  const createText = query.trim();
  const canCreate =
    !!onCreate &&
    createText.length > 0 &&
    !options.some((o) => o.label.trim().toLowerCase() === createText.toLowerCase());

  async function createOption(close: () => void) {
    if (!onCreate || !canCreate || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await onCreate(createText);
      if (created) onChange(created);
      close();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  /** First enabled option index walking from `from` in direction `dir` (wraps). */
  function firstEnabled(from: number, dir: 1 | -1): number {
    const n = filtered.length;
    if (n === 0) return -1;
    let i = from;
    for (let step = 0; step < n; step++) {
      if (i < 0) i = n - 1;
      if (i >= n) i = 0;
      if (!filtered[i].disabled) return i;
      i += dir;
    }
    return -1;
  }

  // Keep the active row anchored to the search results.
  useEffect(() => {
    if (!isOpen || !query) return;
    setActiveIndex(filtered.findIndex((o) => !o.disabled));
  }, [query, isOpen, filtered]);

  useEffect(() => {
    if (activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function selectOption(opt: SelectOption, close: () => void) {
    if (opt.disabled) return;
    onChange(opt.value);
    close();
  }

  /** Shared keyboard navigation for the trigger (no search) and the search input. */
  function onNavKeyDown(e: KeyboardEvent, close: () => void) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => firstEnabled(i + 1, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => firstEnabled(i - 1, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled(0, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(firstEnabled(filtered.length - 1, -1));
        break;
      case 'Enter': {
        e.preventDefault();
        const opt = filtered[activeIndex];
        if (opt) selectOption(opt, close);
        else if (canCreate) void createOption(close);
        break;
      }
      default: {
        // Typeahead (only without a search input, where typing has no field).
        if (hasSearch || e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
        const ta = typeahead.current;
        window.clearTimeout(ta.timer);
        ta.buffer += e.key.toLowerCase();
        ta.timer = window.setTimeout(() => {
          ta.buffer = '';
        }, 600);
        const idx = filtered.findIndex(
          (o) => !o.disabled && o.label.toLowerCase().startsWith(ta.buffer),
        );
        if (idx >= 0) setActiveIndex(idx);
      }
    }
  }

  const activeId = activeIndex >= 0 ? `${uid}-opt-${activeIndex}` : undefined;

  return (
    <div className={className}>
      {!hideLabel && (
        <label id={labelId} className="ui-field-label">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <Popover
        width={width}
        matchTriggerWidth
        onOpenChange={(open) => {
          setIsOpen(open);
          if (open) {
            setQuery('');
            setCreateError(null);
            setActiveIndex(options.findIndex((o) => o.value === value));
          }
        }}
        trigger={({ open, toggle, ref }) => (
          <button
            type="button"
            ref={(el) => ref(el)}
            onClick={toggle}
            onKeyDown={(e) => {
              if (disabled) return;
              if (!open) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  toggle();
                }
                return;
              }
              onNavKeyDown(e, toggle);
            }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-activedescendant={open ? activeId : undefined}
            aria-labelledby={hideLabel ? undefined : labelId}
            aria-label={hideLabel ? label : undefined}
            aria-invalid={error ? true : undefined}
            aria-required={required || undefined}
            className={[
              'ui-select-trigger',
              sm ? 'ui-select-trigger-sm' : '',
              error ? 'border-red-400 focus:border-red-400 focus:ring-red-400/40' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {selected ? (
              <span className="flex min-w-0 items-center gap-2">
                {selected.color && (
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: selected.color }}
                  />
                )}
                {selected.icon && <span className="shrink-0">{selected.icon}</span>}
                <span className="truncate">{selected.label}</span>
              </span>
            ) : (
              <span className="truncate text-gray-400">
                {loading ? t('ui.select.loading') : (placeholder ?? '—')}
              </span>
            )}
            <span className="ml-auto flex shrink-0 items-center gap-1">
              {clearable && selected && value !== '' && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={t('ui.select.clear')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange('');
                  }}
                  className="rounded p-0.5 text-gray-400 hover:bg-accent hover:text-gray-600"
                >
                  <X size={sm ? 12 : 14} />
                </span>
              )}
              <ChevronDown size={sm ? 12 : 14} className="opacity-60" />
            </span>
          </button>
        )}
      >
        {(close) => (
          <div className="flex max-h-72 flex-col">
            {hasSearch && (
              <div className="flex items-center gap-2 border-b border-gray-200 px-2.5 py-2">
                <Search size={14} className="pointer-events-none shrink-0 text-gray-400" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => onNavKeyDown(e, close)}
                  // Not a form field: keep Chrome from guessing a card/name autofill.
                  type="search"
                  name="firmio-picker-search"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  placeholder={t('ui.select.searchPlaceholder')}
                  aria-controls={listboxId}
                  aria-activedescendant={activeId}
                  className="w-full bg-transparent [&::-webkit-search-cancel-button]:hidden text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                />
              </div>
            )}
            <div
              id={listboxId}
              role="listbox"
              aria-labelledby={hideLabel ? undefined : labelId}
              className="overflow-y-auto py-1"
            >
              {loading && (
                <p className="px-3 py-2 text-sm text-gray-400">{t('ui.select.loading')}</p>
              )}
              {!loading && filtered.length === 0 && !canCreate && (
                <p className="px-3 py-2 text-sm text-gray-400">
                  {emptyText ?? t('ui.select.noResults')}
                </p>
              )}
              {!loading &&
                filtered.map((opt, i) => {
                  const isOn = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      id={`${uid}-opt-${i}`}
                      ref={(el) => {
                        optionRefs.current[i] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={isOn}
                      disabled={opt.disabled}
                      onClick={() => selectOption(opt, close)}
                      onMouseEnter={() => {
                        if (!opt.disabled) setActiveIndex(i);
                      }}
                      className={[
                        'ui-menu-item justify-between',
                        i === activeIndex ? 'bg-accent text-accent-foreground' : '',
                        opt.disabled ? 'cursor-not-allowed opacity-50 hover:bg-transparent' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {opt.color && (
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: opt.color }}
                          />
                        )}
                        {opt.icon && <span className="shrink-0">{opt.icon}</span>}
                        <span className="flex min-w-0 flex-col items-start">
                          <span className="w-full truncate text-left">{opt.label}</span>
                          {opt.description && (
                            <span className="w-full truncate text-left text-xs text-gray-400">
                              {opt.description}
                            </span>
                          )}
                        </span>
                      </span>
                      {isOn && <Check size={14} className="shrink-0 text-gray-400" />}
                    </button>
                  );
                })}
              {!loading && canCreate && (
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => void createOption(close)}
                  className={[
                    'ui-menu-item text-brand-700',
                    filtered.length > 0 ? 'border-t border-gray-200' : '',
                    activeIndex < 0 || filtered.length === 0 ? 'bg-accent' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {creating ? (
                    <Loader2 size={14} className="shrink-0 animate-spin" />
                  ) : (
                    <Plus size={14} className="shrink-0" />
                  )}
                  <span className="truncate">{t('ui.select.create', { name: createText })}</span>
                </button>
              )}
              {createError && <p className="px-3 py-1.5 text-xs text-red-600">{createError}</p>}
            </div>
          </div>
        )}
      </Popover>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
